import React, {useEffect, useState, useRef, useMemo} from 'react';
import { PageHeader } from "../../components/PageHeader";
import Section from "../../components/section/Section";
import { selectCodesByParentId } from "../../api/code/CodeService";
import { RecursiveList } from "../../components/recursivelist/RecursiveList";
import { CodeSpecNum } from "../../constant/CodeSpecNum";
import { getAssetByCodeId } from "../../api/static/StaticService";
import {updateAssetLocations} from "../../api/asset/AssetService";
import {getImagesAndFiles} from "../../api/file/FileService";

const getDistance = (a, b) => {
    return Math.sqrt(Math.pow(a.posX - b.posX, 2) + Math.pow(a.posY - b.posY, 2));
};

const BG_COLOR_PALETTE = [
    'bg-red-500',        // 빨강
    'bg-blue-500',       // 파랑
    'bg-green-500',      // 초록
    'bg-yellow-400',     // 노랑
    'bg-purple-500',     // 보라
    'bg-pink-500',       // 분홍
    'bg-orange-500',     // 주황
    'bg-teal-500',       // 청록
    'bg-indigo-500',     // 남색
    'bg-lime-400',       // 라임
    'bg-cyan-400',       // 시안
    'bg-rose-500',       // 장미
    'bg-amber-500',      // 호박색
    'bg-emerald-500',    // 에메랄드
    'bg-violet-500',     // 제비꽃
    'bg-fuchsia-500',    // 자홍
    'bg-sky-500',        // 하늘색
    'bg-slate-500',      // 슬레이트
    'bg-zinc-500',       // 징크
    'bg-stone-500',      // 스톤
    'bg-red-600',        // 진빨강
    'bg-blue-600',       // 진파랑
    'bg-green-600',      // 진초록
    'bg-purple-600',     // 진보라
    'bg-orange-600',     // 진주황
    'bg-teal-600',       // 진청록
    'bg-indigo-600',     // 진남색
    'bg-pink-600',       // 진분홍
    'bg-emerald-600',    // 진에메랄드
    'bg-violet-600',     // 진제비꽃
    'bg-amber-600',      // 진호박색
    'bg-rose-600',       // 진장미
    'bg-fuchsia-600',    // 진자홍
    'bg-sky-600',        // 진하늘색
    'bg-lime-500',       // 진라임
    'bg-cyan-500',       // 진시안
];

const TEXT_COLOR_PALETTE = [
    'text-red-600',
    'text-blue-600',
    'text-green-600',
    'text-yellow-600',
    'text-purple-600',
    'text-pink-600',
    'text-orange-600',
    'text-teal-600',
    'text-indigo-600',
    'text-lime-600',
    'text-cyan-600',
    'text-rose-600',
    'text-amber-700',
    'text-emerald-600',
    'text-violet-600',
    'text-fuchsia-600',
    'text-sky-600',
    'text-slate-700',
    'text-zinc-700',
    'text-stone-700',
    'text-red-700',
    'text-blue-700',
    'text-green-700',
    'text-purple-700',
    'text-orange-700',
    'text-teal-700',
    'text-indigo-700',
    'text-pink-700',
    'text-emerald-700',
    'text-violet-700',
    'text-amber-800',
    'text-rose-700',
    'text-fuchsia-700',
    'text-sky-700',
    'text-lime-700',
    'text-cyan-700',
];



function StaticAssetMap() {
    const [code, setCode] = useState([]);
    const [selectedCodeId, setSelectedCodeId] = useState(null);
    const [selectedLocationName, setSelectedLocationName] = useState('');
    const [floorPlanUrl, setFloorPlanUrl] = useState(null);
    const [assets, setAssets] = useState([]);
    const containerRef = useRef(null);
    const serverUrl = import.meta.env.VITE_IMG_BASE;
    const [activeClusterId, setActiveClusterId] = useState(null);

    const groupColorMap = useMemo(() => {
        const bgMap = {};
        const textMap = {};

        // 중복 제거 및 정렬로 일관성 유지
        const uniqueGroups = [...new Set(assets.map(a => a.groupName).filter(Boolean))].sort();

        uniqueGroups.forEach((groupName, index) => {
            const colorIndex = index % BG_COLOR_PALETTE.length;
            bgMap[groupName] = BG_COLOR_PALETTE[colorIndex];
            textMap[groupName] = TEXT_COLOR_PALETTE[colorIndex];
        });

        return { bg: bgMap, text: textMap };
    }, [assets]);

    // 자산의 색상을 반환하는 헬퍼 함수
    const getAssetColor = (asset) => {
        if (!asset.groupName) return 'bg-gray-500'; // 그룹명 없으면 회색
        return groupColorMap.bg[asset.groupName] || 'bg-gray-500';
    };

    const getAssetTextColor = (asset) => {
        if (!asset.groupName) return 'text-gray-500';
        return groupColorMap.text[asset.groupName] || 'text-gray-500';
    };


    useEffect(() => {
        selectCodesByParentId(CodeSpecNum.AssetLocation).then(response => {
            setCode(response);
            if (response && response.length > 0) {
                changeTargetCodeHandler(response[0]);
            }
        });
    }, []);

    const clusters = useMemo(() => {
        const placedAssets = assets.filter(a => a.posX != null);
        const groups = [];
        const CLUSTER_RADIUS = 20; // 20px 이내면 뭉침

        placedAssets.forEach(asset => {
            const nearbyGroup = groups.find(group => {
                const leader = group.items[0];
                return getDistance(leader, asset) < CLUSTER_RADIUS;
            });

            if (nearbyGroup) {
                nearbyGroup.items.push(asset);
            } else {
                groups.push({
                    id: `cluster-${asset.id}`,
                    items: [asset],
                    posX: asset.posX,
                    posY: asset.posY
                });
            }
        });
        return groups;
    }, [assets]);

    async function changeTargetCodeHandler(value) {
        setSelectedCodeId(value.id);
        setSelectedLocationName(value.name || value.text || '');
        setFloorPlanUrl(null);

        try {
            const response = await getImagesAndFiles("location", value.id, "IMG");
            if (response.status === 200) {
                if (response.data && response.data.length > 0) {
                    const fileData = response.data[0];
                    const fullUrl = serverUrl + fileData.path + fileData.uploadName;
                    setFloorPlanUrl(fullUrl);
                }
            }
        } catch (e) {
            console.error("이미지 로딩 실패:", e);
        }

        getAssetByCodeId(value.id).then(r => {
            setAssets(r.data.list || []);
        }).catch(err => {
            setAssets([]);
        });
    }

    const handlePrint = () => {
        window.print();
    };

    // --- 드래그 앤 드롭 핸들러 ---

    const handleDragStart = (e, assetId) => {
        e.dataTransfer.setData('assetId', assetId);
        e.dataTransfer.effectAllowed = "move";
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    };

    // [지도에 드롭] -> 배치 처리
    const handleDropOnMap = (e) => {
        e.preventDefault();
        const assetId = Number(e.dataTransfer.getData('assetId'));
        const container = containerRef.current;
        const rect = container.getBoundingClientRect();
        const scrollTop = container.scrollTop;
        const scrollLeft = container.scrollLeft;
        const x = (e.clientX - rect.left) + scrollLeft;
        const y = (e.clientY - rect.top) + scrollTop;

        setAssets(prev => prev.map(item =>
            item.id === assetId ? { ...item, posX: x, posY: y } : item
        ));
    };

    // 배치 취소 (공통 함수)
    // 자산의 좌표를 null로 만들어서 미배치 리스트로 보냄
    const cancelPlacement = (assetId) => {
        setAssets(prev => prev.map(item =>
            item.id === assetId ? { ...item, posX: null, posY: null } : item
        ));
    };

    // 우클릭 핸들러
    const handleContextMenu = (e, assetId) => {
        e.preventDefault(); // 브라우저 메뉴 뜨는거 방지
        if(window.confirm("이 자산의 배치를 취소하시겠습니까?")) {
            cancelPlacement(assetId);
        }
    };

    // 리스트에 드롭 핸들러 (드래그 반환)
    const handleDropOnList = (e) => {
        e.preventDefault();
        const assetId = Number(e.dataTransfer.getData('assetId'));
        // 미배치 리스트 영역에 드롭하면 좌표 삭제
        cancelPlacement(assetId);
    }


    const handleSave = () => {
        const itemsToUpdate = assets
            .filter(a => a.posX !== null && a.posX !== undefined)
            .map(a => ({ id: a.id, posX: a.posX, posY: a.posY }));

        updateAssetLocations(itemsToUpdate).then((res) => {
            if(res.status === 200) alert(`${itemsToUpdate.length}건 저장 완료`);
        });
    };

    const placedAssets = useMemo(() =>
            assets.filter(a => a.posX != null && a.posX !== undefined),
        [assets]
    );

    return (
        <>
        <div className={`common-container print:hidden`}>
            <PageHeader header={"자산배치도"} indicator={`자산관리 > 자산현황관리 > 자산배치도`} />

            <Section className={'flex mb-none h-[calc(100vh-210px)]'}>
                <div className={`w-[300px] mr-[5px] h-full border overflow-y-scroll p-1`}>
                    {code && <RecursiveList data={code} setTarget={changeTargetCodeHandler} readOnly />}
                </div>

                <div className="flex flex-1 overflow-hidden relative h-full">

                    {/* 1. 작업 영역 (Drop Zone) */}
                    <div
                        ref={containerRef}
                        className="flex-1 bg-gray-200 relative overflow-hidden"
                        onDragOver={handleDragOver}
                        onDrop={handleDropOnMap}
                    >
                        {floorPlanUrl ? (
                            <img
                                src={floorPlanUrl}
                                alt="Floor Plan"
                                draggable={false}
                                className="w-full h-full object-fill"
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                        ) : (
                            <div className="absolute inset-4 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg pointer-events-none">
                                <span className="text-gray-400 font-bold select-none">
                                    도면이 없습니다.
                                </span>
                            </div>
                        )}

                        {clusters.map((group) => {
                            const isCluster = group.items.length > 1; // 2개 이상 겹쳤는가?
                            const leader = group.items[0];            // 대표 아이콘
                            const isOpen = activeClusterId === group.id; // 팝업 열림 여부

                            // 클러스터 색상 결정: 겹친 자산들이 모두 같은 그룹이면 그 색, 아니면 찐한 바이올렛
                            const allSameGroup = group.items.every(item => item.groupName === leader.groupName);
                            const clusterColor = isCluster && !allSameGroup
                                ? 'bg-violet-900'  // 다른 그룹이 섞여있을때
                                : getAssetColor(leader); // 같은 그룹이거나 단일 자산이면 그룹 색상

                            return (
                                <div
                                    key={group.id}
                                    style={{
                                        position: 'absolute',
                                        left: `${group.posX}px`,
                                        top: `${group.posY}px`,
                                        transform: 'translate(-50%, -50%)',
                                        cursor: isCluster ? 'pointer' : 'move',
                                        zIndex: isOpen ? 100 : 10
                                    }}
                                    // 그룹 클릭 시 팝업 열기/닫기 토글
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (isCluster) setActiveClusterId(isOpen ? null : group.id);
                                    }}
                                    // 그룹이 아닐 때만 바로 드래그 가능
                                    draggable={!isCluster}
                                    onDragStart={(e) => !isCluster && handleDragStart(e, leader.id)}
                                    onContextMenu={(e) => !isCluster && handleContextMenu(e, leader.id)}
                                >
                                    {/* 1. 아이콘 UI */}
                                    <div className="relative flex flex-col items-center w-[80px]">
                                        {/* 동적 색상 적용 */}
                                        <div className={`w-4 h-4 rounded-full border-2 border-white shadow-md ${clusterColor}`}></div>

                                        {/* 겹친 개수 뱃지 */}
                                        {isCluster && (
                                            <div className="absolute -top-2 -right-1 bg-red-600 text-white text-[10px] font-bold px-1.5 rounded-full shadow-sm">
                                                +{group.items.length}
                                            </div>
                                        )}

                                        {/* 라벨: 겹쳐있으면 "N개 겹침", 아니면 이름 */}
                                        <span className="text-xs font-bold bg-white bg-opacity-80 px-1 rounded mt-1 whitespace-nowrap">
                                            {isCluster ? `${group.items.length}개 겹침` : (leader.assetName || leader.name)}
                                        </span>
                                    </div>

                                    {/* 2. [신규] 리스트 팝업 (그룹 클릭 시 노출) */}
                                    {isCluster && isOpen && (
                                        <div
                                            className="absolute top-6 left-1/2 -translate-x-1/2 bg-white border border-gray-300 shadow-xl rounded w-[200px] p-2 z-50 cursor-default"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <div className="text-xs font-bold text-gray-500 mb-2 pb-1 border-b flex justify-between">
                                                <span>자산 목록 ({group.items.length})</span>
                                                <button onClick={() => setActiveClusterId(null)}>✕</button>
                                            </div>
                                            <div className="max-h-[150px] overflow-y-auto space-y-1">
                                                {group.items.map(item => (
                                                    <div
                                                        key={item.id}
                                                        draggable // 팝업 안에서도 드래그 가능!
                                                        onDragStart={(e) => handleDragStart(e, item.id)}
                                                        onContextMenu={(e) => handleContextMenu(e, item.id)}
                                                        className="p-2 bg-gray-50 hover:bg-blue-50 rounded border cursor-grab flex justify-between"
                                                    >
                                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                                            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${getAssetColor(item)}`}></div>
                                                            <span className="text-xs truncate">{item.assetName}</span>
                                                        </div>
                                                        <span className="text-gray-300 text-[10px] ml-2">이동</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* 2. 미배치 자산 리스트 (오른쪽 패널) */}
                    {selectedCodeId && (
                        <div
                            className="w-[250px] border-l bg-white h-full overflow-y-auto z-20 flex-shrink-0 flex flex-col"
                            onDragOver={handleDragOver}
                            onDrop={handleDropOnList}
                        >
                            <div className="sticky top-0 z-10 h-[50px] px-3 bg-gray-100 border-b flex items-center justify-between flex-shrink-0">
                                <span className="font-bold text-sm text-gray-700">
                                    미배치 ({assets.filter(a => a.posX == null).length})
                                </span>
                                <div className="flex gap-2">
                                    <button
                                        onClick={handlePrint}
                                        className="bg-gray-600 hover:bg-gray-700 text-white text-xs px-3 py-1.5 rounded font-bold shadow-sm transition-colors"
                                    >
                                        인쇄
                                    </button>
                                    <button
                                        onClick={handleSave}
                                        className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1.5 rounded font-bold shadow-sm transition-colors"
                                    >
                                        저장
                                    </button>
                                </div>
                            </div>

                            {/* 드래그해서 여기 놓으세요 안내 문구 (선택사항) */}
                            <div className="flex-1 p-2 space-y-2 relative">
                                {assets.filter(asset => asset.posX == null).map(asset => (
                                    <div
                                        key={asset.id}
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, asset.id)}
                                        className="p-3 border rounded bg-white shadow-sm cursor-move hover:bg-blue-50 flex items-center justify-between gap-2"
                                    >
                                        <div className="font-bold text-sm">{asset.assetName}</div>
                                        <div className={`text-sx ${getAssetTextColor(asset)}`}>{asset.groupName}</div>
                                    </div>
                                ))}

                                {/* 리스트가 비었을 때 드래그 유도 문구 */}
                                {assets.filter(a => a.posX == null).length === 0 && (
                                    <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-xs pointer-events-none p-4 text-center">
                                        배치된 자산을 이쪽으로 끌어오면<br/>배치가 취소됩니다.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </Section>
        </div>

        {/* ========== 인쇄용 레이아웃 (화면에서는 숨김) ========== */}
            <div className="hidden print:block" style={{ width: '100vw', height: '100vh' }}>
                {/* 1페이지: 도면 */}
                <div style={{ padding: '0' }}>

                    {floorPlanUrl && (
                        <div
                            className="print-map-container"
                            style={{
                                position: 'relative',
                                width: '277mm',      // A4 landscape - 여백
                                height: '190mm',     // A4 landscape - 여백
                                backgroundColor: '#e5e7eb',
                                margin: '0 auto'     // 중앙 정렬
                            }}
                        >
                            <img
                                src={floorPlanUrl}
                                alt="Floor Plan"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'fill',
                                    border: '1px solid #ccc'
                                }}
                            />

                            <div style={{
                                position: 'absolute',
                                top: '10px',
                                left: '10px',
                                zIndex: 1000,
                                backgroundColor: 'rgba(255, 255, 255, 0.75)',
                                padding: '6px 12px',
                                borderRadius: '4px',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                color: '#1f2937'
                            }}>
                                {selectedLocationName} · 자산배치도
                            </div>

                            {clusters.map((group) => {
                                const isCluster = group.items.length > 1;
                                const leader = group.items[0];
                                const allSameGroup = group.items.every(item =>
                                    item.groupName === leader.groupName
                                );
                                const clusterColor = isCluster && !allSameGroup
                                    ? 'bg-violet-900'
                                    : getAssetColor(leader);

                                const screenContainer = containerRef.current;
                                const screenImg = screenContainer?.querySelector('img');

                                // 화면 대비 인쇄물 비율 계산
                                const printWidth = 277 * 3.7795; // mm → px (1mm = 3.7795px)
                                const printHeight = 190 * 3.7795;

                                const scaleX = screenImg ? printWidth / screenImg.offsetWidth : 1;
                                const scaleY = screenImg ? printHeight / screenImg.offsetHeight : 1;

                                // 보정된 좌표 계산
                                const printPosX = group.posX * scaleX;
                                const printPosY = group.posY * scaleY;

                                return (
                                    <div
                                        key={group.id}
                                        className="print-marker"
                                        style={{
                                            position: 'absolute',
                                            left: `${printPosX}px`,
                                            top: `${printPosY}px`,
                                            transform: 'translate(-50%, -50%)'
                                        }}
                                    >
                                        {/* 마커 UI */}
                                        <div style={{
                                            position: 'relative',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            width: '80px'
                                        }}>
                                            <div
                                                className={clusterColor}
                                                style={{
                                                    width: '16px',
                                                    height: '16px',
                                                    borderRadius: '50%',
                                                    border: '2px solid white',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                                                }}
                                            ></div>
                                            {isCluster && (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: '-8px',
                                                    right: '-4px',
                                                    fontSize: '10px',
                                                    fontWeight: 'bold',
                                                    color: 'white',
                                                    backgroundColor: '#dc2626',
                                                    padding: '1px 6px',
                                                    borderRadius: '9999px'
                                                }}>
                                                    +{group.items.length}
                                                </div>
                                            )}
                                            <span style={{
                                                fontSize: '12px',
                                                fontWeight: 'bold',
                                                backgroundColor: 'rgba(255,255,255,0.8)',
                                                padding: '2px 4px',
                                                borderRadius: '4px',
                                                marginTop: '4px',
                                                whiteSpace: 'nowrap'
                                            }}>
                                    {isCluster
                                        ? `${group.items.length}개 겹침`
                                        : (leader.assetName || leader.name)
                                    }
                                </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* 2페이지: 자산 목록 - 동일 */}
                <div style={{ padding: '10mm' }}>
                    <h2 style={{
                        fontSize: '18px',
                        fontWeight: 'bold',
                        marginBottom: '12px',
                        borderBottom: '2px solid #000',
                        paddingBottom: '8px'
                    }}>
                        {selectedLocationName} 배치된 자산 목록
                    </h2>

                    <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        border: '1px solid #000',
                        fontSize: '11px'
                    }}>
                        <thead>
                        <tr style={{ backgroundColor: '#f3f4f6' }}>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'left', width: '40px' }}>No</th>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'left' }}>자산명</th>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'left', width: '130px' }}>자산그룹</th>
                        </tr>
                        </thead>
                        <tbody>
                        {placedAssets.map((asset, index) => (
                            <tr key={asset.id}>
                                <td style={{ border: '1px solid #000', padding: '5px', textAlign: 'center' }}>{index + 1}</td>
                                <td style={{ border: '1px solid #000', padding: '5px' }}>{asset.assetName}</td>
                                <td style={{ border: '1px solid #000', padding: '5px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <span
                                className={getAssetColor(asset)}
                                style={{
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    display: 'inline-block'
                                }}
                            ></span>
                            {asset.groupName}
                        </span>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>

                    <div style={{ marginTop: '12px', fontSize: '11px', color: '#666' }}>
                        총 {placedAssets.length}건
                    </div>
                </div>
            </div>

            {/* ========== 인쇄 전용 CSS ========== */}
            <style>{`
            @media print {
                @page {
                    size: A4 landscape;
                    margin: 10mm;
                }
                
                body, html {
                    print-color-adjust: exact;
                    -webkit-print-color-adjust: exact;
                    margin: 0;
                    padding: 0;
                }
                
                .print-map-container {
                    width: 277mm !important;   /* A4 landscape 너비 - 여백 */
                    height: 190mm !important;  /* A4 landscape 높이 - 여백 */
                    position: relative !important;
                    page-break-after: always;
                }
                
                .print-map-container img {
                    width: 100% !important;
                    height: 100% !important;
                    object-fit: fill !important;
                }
                
                /* 마커 위치는 절대 좌표 그대로 사용 */
                .print-marker {
                    position: absolute !important;
                    transform: translate(-50%, -50%) !important;
                }
                
                table { 
                    page-break-inside: auto;
                }
                tr { 
                    page-break-inside: avoid;
                    page-break-after: auto;
                }
                thead { 
                    display: table-header-group;
                }
            }
        `}</style>
        </>
    );
}

export default StaticAssetMap;